import React from "react";
import { permissionProfiles } from "../components/Authorize/permissionProfiles";
import { tenantFeatureFlagNames } from "../features/saasManagement/TenantConfig/tenantConfigConsts";
import {
    IconBox,
    IconCar,
    IconClipboard,
    IconForm,
    IconGraph,
    IconLot,
    IconReservations,
    IconSla,
    IconTasks,
    IconTruck,
    IconUser,
    IconVendors,
} from "../icons";

export const buildMenu = ({
    isSaasManager,
    isUsingTransports,
    isUsingWashes,
    invoiceUrl,
    showUsers,
}) => {
    const regular = [
        {
            key: "dashboard",
            text: "Dashboard",
            link: "dashboard",
            icon: <IconGraph />,
            children: [
                { key: "dash.washes", text: "Washes", link: "dashboard/newWash", checkVisibility: () => isUsingWashes },
                { key: "dash.dispatch", text: "Dispatch", link: "dashboard/dispatch", profile: permissionProfiles.DASHBOARD.DISPATCH_DASHBOARD_VIEW },
                { key: "dash.kpi", text: "KPI", link: "dashboard/kpi", profile: permissionProfiles.DASHBOARD.KPI_DASHBOARD_VIEW, featureFlagName: tenantFeatureFlagNames.UseLotKpis },
                { key: "dash.activity", text: "Activities", link: "dashboard/activity" },
                { key: "dash.fleet", text: "Fleet Management", link: "dashboard/fleetManagement" },
                { key: "dash.loads", text: "Loads", link: "dashboard/loads" },
            ],
        },
        {
            key: "assets",
            text: "Assets",
            icon: <IconCar />,
            profile: permissionProfiles.ASSETS.ASSETS_PAGE_VIEW_ASSET_TABLE,
            link: "assets",
        },
        {
            key: "workflows",
            text: "Workflows",
            icon: <IconTasks />,
            profile: permissionProfiles.ISSUETRACKER.ISSUE_TRACKER_VIEW,
            link: "workflow",
        },
        {
            key: "loads",
            text: "Loads",
            icon: <IconTruck />,
            checkVisibility: () => isUsingTransports,
            children: [
                { key: "loads.dashboard", text: "Loads Dashboard", link: "dashboard/loads" },
                { key: "loads.list", text: "Load List", link: "loads/list" },
                { key: "loads.create", text: "Create Load", link: "loads/list?createload=true&latestStatus=8", profile: permissionProfiles.LOAD.CREATE_LOAD },
            ],
        },
        {
            key: "lotmgmt",
            text: "Lot Management",
            labelLines: ["Lots", "Management"],
            icon: <IconLot />,
            children: [
                {
                    key: "lot.lots",
                    text: "Lot List",
                    link: "lots/list",
                    icon: <IconLot />,
                    children: [
                        { key: "lot.lots.access", text: "Access Control", link: "access", featureFlagName: tenantFeatureFlagNames.UseLotAccess },
                        { key: "lot.lots.door", text: "Door Release", link: "doorRelease", profile: permissionProfiles.ACCESS.DOOR_RELEASE_LIST, featureFlagName: tenantFeatureFlagNames.UseLotAccess },
                    ],
                },
                {
                    key: "lot.ops",
                    text: "Lot Operations",
                    icon: <IconClipboard />,
                    profile: permissionProfiles.DASHBOARD.SHOW_LOTS_OPERATIONS_SUB_MENU,
                    children: [
                        { key: "lot.ops.tasks", text: "Tasks (Report)", link: "tasks", profile: permissionProfiles.TASKS.SUPERS_TASK_LIST, featureFlagName: tenantFeatureFlagNames.UseUserTasks },
                        { key: "lot.ops.inspections", text: "Inspections (Report)", link: "inspection", profile: permissionProfiles.INSPECTION.INSPECTION_ACCESS, featureFlagName: tenantFeatureFlagNames.UseInspections },
                        { key: "lot.ops.newinspection", text: "New Inspection", link: "inspection/new", profile: permissionProfiles.INSPECTION.INSPECTION_CREATE, featureFlagName: tenantFeatureFlagNames.UseInspections },
                        { key: "lot.ops.oneonone", text: "1:1 Report", link: "oneonones", profile: permissionProfiles.DASHBOARD.ONE_ON_ONE, featureFlagName: tenantFeatureFlagNames.UseOneOnOne },
                    ],
                },
                {
                    key: "lot.inventory",
                    text: "Inventory",
                    icon: <IconBox />,
                    profile: permissionProfiles.INVENTORY.INVENTORY_ACCESS,
                    children: [{ key: "lot.inventory.parts", text: "Parts & Supplies", link: "inventory" }],
                },
            ],
        },
        {
            key: "billing",
            text: "Billing & Finance",
            labelLines: ["Billing &", "Finance"],
            icon: <IconSla />,
            profile: permissionProfiles.DASHBOARD.SHOW_BILLING_SUB_MENU,
            children: [
                {
                    key: "bill.invoices",
                    text: "Invoices",
                    icon: <IconSla />,
                    profile: permissionProfiles.DASHBOARD.SHOW_BILLING_INVOICES_SUB_MENU,
                    children: [
                        { key: "bill.invoices.client", text: "Client Invoices", link: invoiceUrl, profile: permissionProfiles.INVOICE.INVOICES_VIEW, featureFlagName: tenantFeatureFlagNames.UseInvoices },
                        { key: "bill.invoices.vendor", text: "Vendor Invoices", link: "invoices/vendor", profile: permissionProfiles.INVOICE.VIEW_VENDOR_INVOICE, featureFlagName: tenantFeatureFlagNames.UseInvoices },
                    ],
                },
                {
                    key: "bill.reports",
                    text: "Financial Reports",
                    icon: <IconClipboard />,
                    profile: permissionProfiles.DASHBOARD.SHOW_BILLING_FINANCIAL_REPORTS_SUB_MENU,
                    children: [
                        { key: "bill.reports.storage", text: "Storage Reports", link: "storage", profile: permissionProfiles.REPORTS.VIEW_STORAGE_REPORTS },
                        { key: "bill.reports.margin", text: "Margin Analysis", link: "invoices/marginAnalysis", profile: permissionProfiles.INVOICE.INVOICES_VIEW, featureFlagName: tenantFeatureFlagNames.UseInvoices },
                    ],
                },
                {
                    key: "bill.config",
                    text: "Billing Configuration",
                    icon: <IconClipboard />,
                    profile: permissionProfiles.DASHBOARD.SHOW_BILLING_CONFIG_REPORTS_SUB_MENU,
                    children: [
                        { key: "bill.config.pricing", text: "Price Database", link: "pricing", profile: permissionProfiles.PRICING.MANAGE_PPRICING },
                        { key: "bill.config.pricingManagement", text: 'Pricing Management', link: 'pricingManagement', profile: permissionProfiles.PRICING.MANAGE_PPRICING, },
                        { key: "bill.config.costcodes", text: "Cost Codes", link: "costcodes", profile: permissionProfiles.REPORTS.VIEW_COST_CODES_PAGE, featureFlagName: tenantFeatureFlagNames.UseInvoices },
                        { key: "bill.config.qb", text: "QB Mapping", link: "quickBooksAccounts", profile: permissionProfiles.INVOICE.VIEW_QUICKBOOKS_ACCOUNT_MAPPING, featureFlagName: tenantFeatureFlagNames.UseInvoices },
                        { key: "bill.config.tax", text: "Tax Rates", link: "taxrate", profile: permissionProfiles.TAX_RATE.TAX_VIEW, featureFlagName: tenantFeatureFlagNames.UseInvoices },
                    ],
                },
            ],
        },
        {
            key: "admin",
            text: "Admin",
            icon: <IconUser />,
            profile: permissionProfiles.DASHBOARD.SHOW_ADMIN_SUB_MENU,
            children: [
                { key: "admin.users", text: "Users", icon: <IconUser />, link: "userManagement", profile: permissionProfiles.USER_MANAGEMENT.ACCESS, checkVisibility: () => showUsers || true },
                { key: "admin.clientconfig", text: "Client Config & SLAs", icon: <IconSla />, link: "slas" },
                { key: "admin.forms", text: "Forms (Client SOPs)", icon: <IconForm />, link: "forms", profile: permissionProfiles.FORMS.CREATE_FORMS, featureFlagName: tenantFeatureFlagNames.UseMobileApp },
                { key: "admin.vendors", text: "Vendors", icon: <IconVendors />, link: "vendors", profile: permissionProfiles.VENDOR.VIEW },
                { key: "admin.res", text: "Reservations", icon: <IconReservations />, link: "reservations", profile: permissionProfiles.RESERVATIONS.VIEW, children: [{ key: "admin.res.carriers", text: "Carriers", link: "carriers", profile: permissionProfiles.CARRIERS.VIEW }] },
            ],
        },
    ];

    const saas = [{ key: "tenants", text: "Tenants", icon: <IconLot />, link: "saas" }];
    return isSaasManager ? saas : regular;
};

export const findActiveMenuNode = (items, pathname, search) => {
    const allNodes = [];

    const flatten = (nodes) => {
        for (const n of nodes) {
            allNodes.push(n);
            if (n.children) flatten(n.children);
        }
    };
    flatten(items);

    const matches = (node) => {
        if (!node.link) return false;

        const [path, query] = String(node.link).split("?");
        const pathMatch = pathname === `/${path}` || pathname.startsWith(`/${path}/`);
        if (!pathMatch) return false;

        if (!query) {
            if (search) return false;
            return true;
        }

        const required = new URLSearchParams(query);
        const actual = new URLSearchParams(search || "");
        for (const [k, v] of required.entries()) {
            if (actual.get(k) !== v) return false;
        }
        return true;
    };

    const queryFirst = allNodes.filter((n) => String(n.link || "").includes("?"));
    for (const n of queryFirst) if (matches(n)) return n;

    const baseNext = allNodes.filter((n) => n.link && !String(n.link).includes("?"));
    for (const n of baseNext) if (matches(n)) return n;

    return null;
};